/**
 * Responsive image helpers.
 *
 * Stock photo CDNs (Unsplash, Pexels) and our own object storage all accept width parameters, so
 * one source URL can serve a WebP ladder instead of a single oversized JPEG.
 */
import { resolveImageUrl } from './utils';

const WIDTHS = [480, 768, 1024, 1400, 1920];

const withParams = (url, width) => {
  try {
    const u = new URL(url, window.location.origin);
    if (u.hostname.includes('unsplash.com')) {
      u.searchParams.set('auto', 'format');
      u.searchParams.set('fit', 'crop');
      u.searchParams.set('fm', 'webp');
      u.searchParams.set('q', '72');
      u.searchParams.set('w', String(width));
      return u.toString();
    }
    if (u.hostname.includes('pexels.com')) {
      u.searchParams.set('auto', 'compress');
      u.searchParams.set('cs', 'tinysrgb');
      u.searchParams.set('w', String(width));
      return u.toString();
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Props for an <img>: WebP srcSet where the host supports it, plus intrinsic size so the browser
 * reserves the box before the bytes land (no layout shift).
 */
export function imgProps(src, { width = 1400, height, sizes = '100vw', eager = false } = {}) {
  const resolved = resolveImageUrl(src);
  if (!resolved) return { src: '' };
  const ladder = WIDTHS.filter(w => w <= width * 1.4)
    .map(w => [w, withParams(resolved, w)])
    .filter(([, url]) => url);

  return {
    src: withParams(resolved, width) || resolved,
    ...(ladder.length > 1 ? { srcSet: ladder.map(([w, url]) => `${url} ${w}w`).join(', '), sizes } : {}),
    width,
    height: height || Math.round(width * 0.66),
    loading: eager ? 'eager' : 'lazy',
    decoding: eager ? 'sync' : 'async',
    ...(eager ? { fetchPriority: 'high' } : {}),
  };
}

/** Card-sized image (grid tiles): never needs more than ~700px of real pixels. */
export const cardImgProps = (src, opts = {}) =>
  imgProps(src, { width: 768, height: 512, sizes: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw', ...opts });

/** Full-bleed hero / section background. */
export const heroImgProps = (src, opts = {}) =>
  imgProps(src, { width: 1920, height: 1080, sizes: '100vw', ...opts });
